import { useState, useContext } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { AuthContext } from '@/context/AuthContext';
import { useTranslation } from '@/hooks';
import { useBranding } from '@/context/BrandingContext';

const Login = () => {
  const { t } = useTranslation();
  const { login, isAuthenticated, isLoading } = useContext(AuthContext);
  const navigate = useNavigate();
  const { branding } = useBranding();
  const loginLogo = branding.logoUrl;

  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Redirect if already authenticated
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) {
      setError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await login(formData);

      if (response.success) {
        // Redirect to dashboard
        navigate('/', { replace: true });
      } else {
        // Show error message
        setError(response.message);
      }
    } catch (error) {
      console.error('Login error:', error);
      setError(t.auth.login.unexpectedError);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show loading spinner while checking authentication status
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="relative isolate flex h-dvh items-center justify-center overflow-hidden bg-gray-100 flex-col p-0 sm:min-h-screen sm:px-6 sm:py-10">
      <div className="w-full px-3 sm:mx-auto sm:max-w-md sm:px-0">
        <div className="bg-white px-5 py-6 shadow sm:rounded-lg sm:px-10 sm:py-8 space-y-5 sm:space-y-6">
          <div className="sm:mx-auto sm:w-full sm:max-w-md">
            <div className="text-center">
              <div className="mx-auto flex h-24 w-44 items-center justify-center sm:h-28 sm:w-56">
                {loginLogo && (
                  <img className="max-h-full w-full object-contain" alt={branding.appName || 'logo'} src={loginLogo} />
                )}
              </div>
              {(branding.appName || branding.appSubtitle) && (
                <div className="mt-3 sm:mt-4 text-gray-600">
                  {branding.appName && (
                    <div className="text-lg font-semibold text-gray-800">{branding.appName}</div>
                  )}
                </div>
              )}
            </div>
          </div>
          <form className="space-y-4 sm:space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <div className="flex">
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">
                      {t.auth.login.loginFailed}
                    </h3>
                    <div className="mt-2 text-sm text-red-700">
                      {error}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700">
                {t.form.username}
              </label>
              <div className="mt-1">
                <input
                  id="username"
                  name="username"
                  type="text"
                  autoComplete="username"
                  required
                  value={formData.username}
                  onChange={handleChange}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder={t.auth.placeholders.username}
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                {t.form.password}
              </label>
              <div className="mt-1">
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder={t.auth.placeholders.password}
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isSubmitting || !formData.username || !formData.password}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {t.buttons.signingIn}
                  </>
                ) : (
                  t.buttons.signIn
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
